import { Injectable } from '@angular/core';
import { User as IFBUser } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  UserInfoInputDialogComponent,
} from '../dialogs/user-info-input-dialog/user-info-input-dialog.component';
import { UserInfoDialogMode } from '../dialogs/user-info-input-dialog/user-info-dialog-mode';
import { LegalAgreements, User } from '../models/user';
import {
  EventCategory,
  SessionEvent,
  SessionEventSubscriber,
} from '../sessions/session/SessionEvent';
import { SpaceRepository } from '../state/space.repository';

import { AuthService } from './auth.service';
import { FlagsService, FLAGS } from './flags.service';
import { RealtimeSpaceService } from './realtime-space.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { UserService } from './user.service';
import { TelemetryService } from './telemetry.service';
import { WbDialogService } from './wb-dialog.service';
import { ModalManagerService } from './modal-manager.service';

// Firebase `sign_in_provider` values whose users are covered by another agreement, so they
// are not asked to accept the legal agreements in-app. Clever, ClassLink, LTI and
// TutorCruncher all sign in with a custom token (`custom`). Don't judge by `providerData`:
// those flows create their Firebase user with a password, so it reads `password`.
const LEGAL_AGREEMENTS_EXEMPT_PROVIDERS: ReadonlySet<string> = new Set(['custom', 'anonymous']);
// SAML / OIDC SSO providers are named `saml.<id>` / `oidc.<id>`.
const LEGAL_AGREEMENTS_EXEMPT_PROVIDER_PREFIXES: readonly string[] = ['saml.', 'oidc.'];

@Injectable({
  providedIn: 'root',
})
export class UserInfoInputDialogService implements SessionEventSubscriber {
  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private spaceRepo: SpaceRepository,
    private realtimeSpaceService: RealtimeSpaceService,
    private sharedDataService: SessionSharedDataService,
    private telemetry: TelemetryService,
    private wbDialogService: WbDialogService,
    private modalManagerService: ModalManagerService,
    private flagsService: FlagsService,
  ) {
    this.sharedDataService.sessionEventController?.subscribeToEvent(this, EventCategory.Users);
  }

  sessionEventReceived(event: SessionEvent, userId: string): void {
    if (event.category === EventCategory.Users && event.type === 'rename-user') {
      // update populate users in space repo
      const updatedSession = this.spaceRepo.activeSpace!;
      if (updatedSession) {
        updatedSession.populatedUsers.forEach((user) => {
          if (user._id === event.data._id) {
            user.name = event.data.name;
          }
        });

        this.spaceRepo.updateSpace(this.spaceRepo.activeSpaceId!, updatedSession);
      }
    }
  }

  /** Opens the dialog if the signed-in user still has something to fill in or accept. */
  async openDialogIfNeeded(): Promise<void> {
    // Read before awaiting: the navigation that called us is only "current" until it settles.
    const isSpaceRoute = this.isSpaceRoute();
    const mode = await this.getRequiredMode();
    if (mode) {
      this.openDialog(isSpaceRoute, mode);
    }
  }

  private isSpaceRoute(): boolean {
    // finalUrl is the target after redirects, same as the guard's `state.url`. It is set before
    // guards run; outside a navigation, fall back to the current router url.
    const navigation = this.router.getCurrentNavigation();
    const url = (navigation?.finalUrl ?? navigation?.extractedUrl)?.toString() ?? this.router.url;
    return /\/spaces\/([^/?]+)/.test(url);
  }

  /** What the dialog should ask for, or null if the user has nothing left to do. */
  private async getRequiredMode(): Promise<UserInfoDialogMode | null> {
    const firebaseUser = await firstValueFrom(this.authService.firebaseUser);
    const user = this.userService.user.getValue()?.user;
    if (!firebaseUser || !user || user.isAnonymous || !this.isEmailVerifiedOrOAuth(firebaseUser)) {
      return null;
    }

    const showNameAndRoleSection =
      !user.institution &&
      (!user.info?.questionnaireComplete || !user.name || user.name === 'Anonymous User');
    const showLegalAgreementsSection = await this.shouldShowLegalAgreementsSection(user);

    if (showNameAndRoleSection && showLegalAgreementsSection) {
      return UserInfoDialogMode.NAME_AND_ROLE_AND_LEGAL_AGREEMENTS;
    }
    if (showNameAndRoleSection) {
      return UserInfoDialogMode.NAME_AND_ROLE;
    }
    return showLegalAgreementsSection ? UserInfoDialogMode.LEGAL_AGREEMENTS : null;
  }

  /**
   * Whether the user must accept the Terms of Service and Privacy Policy. The Optimizely flag is
   * our kill switch: when it is off nobody is asked. Everything not exempt is asked, so a newly
   * added sign-in method defaults to showing them. Add further rules here as the policy evolves.
   */
  private async shouldShowLegalAgreementsSection(user: User): Promise<boolean> {
    if (
      !this.flagsService.isFlagEnabled(FLAGS.ENABLE_LEGAL_AGREEMENTS_CONSENT) ||
      user.legalAgreements?.acceptedAt
    ) {
      return false;
    }
    // Fetched last: only needed once the cheaper checks say the agreements could be required.
    const signInProvider = await this.authService.getSignInProvider();
    if (!signInProvider) {
      return false;
    }
    const isExempt =
      LEGAL_AGREEMENTS_EXEMPT_PROVIDERS.has(signInProvider) ||
      LEGAL_AGREEMENTS_EXEMPT_PROVIDER_PREFIXES.some((prefix) => signInProvider.startsWith(prefix));
    return !isExempt;
  }

  private isEmailVerifiedOrOAuth(firebaseUser: IFBUser): boolean {
    const providers = firebaseUser.providerData;
    const isPasswordOnly = providers?.length === 1 && providers[0]?.providerId === 'password';
    // Firebase is slow to update emailVerified, so check multiple sources
    return !isPasswordOnly || firebaseUser.emailVerified || this.authService.emailVerified;
  }

  private openDialog(isSpaceRoute: boolean, mode: UserInfoDialogMode): void {
    if (this.wbDialogService.getDialogRefOf(UserInfoInputDialogComponent)) {
      return;
    }

    this.modalManagerService.showModal(
      UserInfoInputDialogComponent,
      {
        panelClass: 'user-info-dialog-panel',
        disableClose: true,
        autoFocus: false,
        data: {
          isSpaceRoute,
          mode,
        },
      },
      {
        afterClosed: (response: {
          user: User;
          roleDescription?: string;
          legalAgreements?: LegalAgreements;
        }) => {
          this.userService.appLoading.next(false);
          if (response) {
            // update current user
            const currentUser = this.userService.user.getValue()!;
            if (mode !== UserInfoDialogMode.LEGAL_AGREEMENTS) {
              currentUser.user.name = response.user.name;
              currentUser.user.info = response.user.info;
            }
            if (response.legalAgreements) {
              currentUser.user.legalAgreements = response.legalAgreements;
            }
            // emit the changes to all components
            this.userService.user.next(currentUser);
            if (mode === UserInfoDialogMode.LEGAL_AGREEMENTS) {
              return;
            }
            this.telemetry.logBasicEvent('user_role_data_provided', {
              roleType: currentUser.user.info?.roleType,
              role: currentUser.user.info?.role,
              roleDescription: response.roleDescription,
            });
            // update populate users in space repo
            const updatedSession = this.spaceRepo.activeSpace!;
            if (updatedSession) {
              updatedSession.populatedUsers.forEach((user) => {
                if (user._id === currentUser.user._id) {
                  user.name = currentUser.user.name;
                }
              });

              this.spaceRepo.updateSpace(this.spaceRepo.activeSpaceId!, updatedSession);
            }

            // send event to all users
            this.realtimeSpaceService.service.sendEvent(
              this,
              new SessionEvent(EventCategory.Users, 'rename-user', {
                _id: currentUser.user._id,
                name: currentUser.user.name,
              }),
            );

            // send intercom event
            this.telemetry.logBasicEvent('spaces_manager_setup_completed');

            // send intercom event space manager loaded for guest users (no institution)
            if (!currentUser.user.institution) {
              this.telemetry.logBasicEvent('space-manager-loaded');
            }
          }
        },
      },
    );
  }
}
